/**
 * Agrégation « retours communs » sur les pages de référence sélectionnées.
 *
 * Regroupe les problèmes (opportunités PageSpeed, erreurs techniques, critères SEO
 * échoués) par identifiant, sur l'ensemble des pages de référence, puis classe :
 *  - systémique : présent sur TOUTES les pages -> problème de fond (souvent le thème/modèle)
 *  - fréquent   : présent sur plusieurs pages mais pas toutes
 *  - spécifique : présent sur une seule page
 *
 * Source unique partagée par l'app (onglet Pages de référence) et le rapport HTML.
 */

import { getRecommendations } from './performanceRecommendations';
import { evaluateSeoCriteria } from './seoCriteria';
import {
  translateLighthouse,
  translateError,
  normalizeCategoryKey,
  SEO_CRITERIA_DICT,
} from './auditDictionary';
import { isOperationalTechnicalError } from './technicalErrors';

/** Poids d'impact par sévérité (pour le tri fréquence x impact). */
const SEVERITY_WEIGHT = { high: 1, medium: 0.6, low: 0.3 };

/** Sévérité par défaut des critères SEO on-page échoués. */
const SEO_CRITERION_SEVERITY = {
  title: 'high',
  meta: 'medium',
  h1: 'medium',
  images: 'high',
  canon: 'medium',
};

function severityFromScore(score) {
  if (score == null) return 'medium';
  if (score <= 0.3) return 'high';
  if (score <= 0.6) return 'medium';
  return 'low';
}

/**
 * Collecte tous les findings d'une page sous une forme normalisée.
 * @returns {Array<{key,label,pourquoi,corriger,effort,axe,severity,impact,source}>}
 */
export function collectPageFindings(page) {
  const out = [];

  // 1. Opportunités PageSpeed
  const recs = getRecommendations(page?.perf?.pagespeed_raw);
  for (const rec of recs) {
    const t = translateLighthouse(rec);
    if (!t) continue;
    out.push({
      key: `lh:${t.id}`,
      label: t.label,
      pourquoi: t.pourquoi,
      corriger: t.corriger,
      effort: t.effort,
      axe: t.axe,
      severity: severityFromScore(rec.score),
      impact: rec.score != null ? 1 - rec.score : 0.5,
      source: 'performance',
    });
  }

  // 2. Erreurs techniques (déjà par page) — hors erreurs outil (PageSpeed API, etc.)
  for (const err of page?.errors || []) {
    if (isOperationalTechnicalError(err)) continue;
    const t = translateError(err);
    if (!t) continue;
    out.push({
      key: `err:${normalizeCategoryKey(err.category)}`,
      label: t.label,
      pourquoi: t.pourquoi,
      corriger: t.corriger,
      effort: t.effort,
      axe: t.axe,
      severity: t.severity,
      impact: SEVERITY_WEIGHT[t.severity] ?? 0.5,
      source: 'error',
    });
  }

  // 3. Critères SEO on-page échoués
  if (page?.seo) {
    const c = evaluateSeoCriteria(page.seo);
    const failing = [];
    if (!c.titleOk) failing.push('title');
    if (!c.metaOk) failing.push('meta');
    if (!c.h1Ok) failing.push('h1');
    if (!c.imagesOk) failing.push('images');
    if (!c.canonOk) failing.push('canon');
    for (const crit of failing) {
      const d = SEO_CRITERIA_DICT[crit];
      if (!d) continue;
      const severity = SEO_CRITERION_SEVERITY[crit] || 'medium';
      out.push({
        key: `seo:${crit}`,
        label: d.label,
        pourquoi: d.pourquoi,
        corriger: d.corriger,
        effort: d.effort,
        axe: d.axe,
        severity,
        impact: SEVERITY_WEIGHT[severity] ?? 0.5,
        source: 'seo',
      });
    }
  }

  return out;
}

/**
 * Construit les retours communs/fréquents/spécifiques sur les pages de référence.
 * @param {Array} reportPages - pages de référence ({ url, seo, perf, errors })
 * @returns {{ systemic:Array, frequent:Array, specific:Array, totalPages:number }}
 */
export function buildCommonFindings(reportPages) {
  const pages = (reportPages || []).filter((p) => p && p.url);
  const totalPages = pages.length;
  if (totalPages === 0) {
    return { systemic: [], frequent: [], specific: [], totalPages: 0 };
  }

  // Regroupement par clé : on dédoublonne par page (un problème compte une fois par page).
  const groups = new Map();
  for (const page of pages) {
    const findings = collectPageFindings(page);
    const seenKeysForPage = new Set();
    for (const f of findings) {
      if (seenKeysForPage.has(f.key)) continue;
      seenKeysForPage.add(f.key);

      if (!groups.has(f.key)) {
        groups.set(f.key, {
          key: f.key,
          label: f.label,
          pourquoi: f.pourquoi,
          corriger: f.corriger,
          effort: f.effort,
          axe: f.axe,
          source: f.source,
          severity: f.severity,
          pages: [],
          impactSum: 0,
        });
      }
      const g = groups.get(f.key);
      g.pages.push(page.url);
      g.impactSum += f.impact;
      // Conserve la sévérité la plus forte rencontrée.
      if ((SEVERITY_WEIGHT[f.severity] ?? 0) > (SEVERITY_WEIGHT[g.severity] ?? 0)) {
        g.severity = f.severity;
      }
    }
  }

  const all = Array.from(groups.values()).map((g) => ({
    ...g,
    nbPages: g.pages.length,
    avgImpact: g.impactSum / g.pages.length,
    sortScore: g.pages.length * (g.impactSum / g.pages.length),
  }));

  const byScore = (a, b) => b.sortScore - a.sortScore;

  const systemic = all.filter((g) => g.nbPages === totalPages && totalPages >= 2).sort(byScore);
  const frequent = all.filter((g) => g.nbPages >= 2 && g.nbPages < totalPages).sort(byScore);
  const specific = all.filter((g) => g.nbPages === 1).sort(byScore);

  return { systemic, frequent, specific, totalPages };
}
